import React, { useEffect, useState } from 'react';
import { Box, StackItem, StackLayout } from '@leagueplatform/genesis-core';
import { getAccessToken } from '@leagueplatform/engen-capability-shared-library/src/utils/api';
import { DynamicModal } from '@leagueplatform/engen-capability-shared-library/src/components/layouts/dynamicModal';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { FooterMultiStepProgress } from '@leagueplatform/engen-capability-shared-library/src/components/step-navigator/footerMultiStepProgress';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { WelcomeComponent } from './pages/welcome/welcome.component';
import { CostLevelComponent } from './pages/cost/cost-level.component';
import { EasyPlanAboutComponent } from './pages/about/easyPlan-about.component';
import { EasyPlanPerkComponent } from './pages/perks/easyPlan-perks-screen.component';
import useEasyPlanStore from '../store/easy-plan-store';
import { EasyPlanHomeInfo, EASY_PLAN_API_TYPE } from '../types/easy-plan.types';
import { easyPlan } from '../easyPlan.config';

import * as constants from '../common/constant';

export function EasyPlan() {
  const componentList: any = [];
  const initialEasyPlanInfo = useEasyPlanStore(
    (state: any) => state.initialEasyPlan,
  );
  const [easyPlanHomneInfo, setEasyPlanHomeInfo] =
    useState<EasyPlanHomeInfo>(initialEasyPlanInfo);
  const saveEasyPlanInformation = useEasyPlanStore(
    (state: any) => state.saveEasyPlanHomeInfo,
  );

  const [easyHomeInfoLoading, setEasyHomeInfoLoading] =
    useState<boolean>(false);
  const [easyHomeApiFailure, setEasyHomeApiFailure] = useState<boolean>(false);
  const [isDisableNext, setIsDisableNext] = useState<boolean>(false);
  const [isValidateForm, setIsValidateForm] = useState<boolean>(false);

  const [isErrorHandling, setErrorHandling] = useState<boolean>(true);
  const navigate = useNavigate();

  const config = easyPlan.getConfig();
  const highmarkURL = config.HIGHMARK_API_URL;
  const easyHomeInfoURL = `${highmarkURL}/digital/v3/members/benefits/easy-plan-home`;

  const customHomeScreen = [
    <WelcomeComponent
      errorHandling={(val: boolean) => setErrorHandling(val)}
      trackAnalytics={() => {
        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
          detail: constants.ANALYTICS_NEXT_STEP,
          product_area: constants.ANALYTICS_PRODUCT_AREA,
          screen_name: `${constants.ANALYTICS_SCREEN_NAME} - ${constants.ANALYTICS_WELCOME}`,
          current_step: 1,
          total_steps: 4,
          path: window.location.pathname,
          event_author: constants.ENGEN,
        });
      }}
    />,
    <CostLevelComponent
      errorHandling={(val: boolean) => setErrorHandling(val)}
      trackAnalytics={() => {
        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
          detail: constants.ANALYTICS_NEXT_STEP,
          product_area: constants.ANALYTICS_PRODUCT_AREA,
          screen_name: `${constants.ANALYTICS_SCREEN_NAME} - ${constants.ANALYTICS_COST}`,
          current_step: 2,
          total_steps: 4,
          path: window.location.pathname,
          event_author: constants.ENGEN,
        });
      }}
    />,
    <EasyPlanPerkComponent
      errorHandling={(val: boolean) => setErrorHandling(val)}
      trackAnalytics={() => {
        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
          detail: constants.ANALYTICS_NEXT_STEP,
          product_area: constants.ANALYTICS_PRODUCT_AREA,
          screen_name: `${constants.ANALYTICS_SCREEN_NAME} - ${constants.ANALYTICS_PERKS}`,
          current_step: 3,
          total_steps: 4,
          path: window.location.pathname,
          event_author: constants.ENGEN,
        });
      }}
    />,
    <EasyPlanAboutComponent
      errorHandling={(val: boolean) => setErrorHandling(val)}
      trackAnalytics={() => {
        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
          detail: constants.ANALYTICS_FINAL_STEP,
          product_area: constants.ANALYTICS_PRODUCT_AREA,
          screen_name: `${constants.ANALYTICS_SCREEN_NAME} - ${constants.ANALYTICS_ABOUT}`,
          current_step: 4,
          total_steps: 4,
          path: window.location.pathname,
          event_author: constants.ENGEN,
        });
      }}
      lastPage
    />,
  ];

  useEffect(() => {
    setEasyHomeInfoLoading(true);
    getAccessToken(easyHomeInfoURL)
      .then((result: any) => {
        setEasyHomeInfoLoading(false);
        if (result?.easyPlanHome) {
          setEasyPlanHomeInfo(result);
          setEasyHomeApiFailure(false);
          setIsDisableNext(false);
        } else if (result?.errMsg) {
          setEasyHomeApiFailure(true);
          setIsDisableNext(true);
        }
      })
      .catch(() => {
        setEasyHomeApiFailure(true);
        setIsDisableNext(true);
        setEasyHomeInfoLoading(false);
      });
  }, [easyHomeInfoURL]);

  useEffect(() => {
    saveEasyPlanInformation(easyPlanHomneInfo);
  }, [easyPlanHomneInfo, saveEasyPlanInformation]);

  if (easyPlanHomneInfo) {
    componentList.push(...customHomeScreen);
  }

  const closeAPIFailureModal = () => {
    navigate(-1);
    setEasyHomeApiFailure(false);
  };

  useEffect(() => {
    if (easyHomeApiFailure) {
      setIsDisableNext(true);
    }
  }, [easyHomeApiFailure, isDisableNext]);
  return (
    <StackLayout
      horizontalAlignment="center"
      css={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: '#F8F8F8',
      }}
      data-testid="easyPlanHome"
    >
      {easyHomeInfoLoading && (
        <StackItem
          css={{
            textAlign: 'center',
            zIndex: '200',
            top: '50px',
            backgroundColor: '#CCCCCC',
            marginBottom: '20px',
          }}
        >
          <LoadingIndicator />
        </StackItem>
      )}
      {easyPlanHomneInfo !== null && (
        <Box css={{ width: '100%' }}>
          <FooterMultiStepProgress
            isValidateForm={(val: boolean) => setIsValidateForm(val)}
            nextButtonClicked={isValidateForm}
            isNextDisabled={isDisableNext}
            list={componentList}
            formName={EASY_PLAN_API_TYPE.EASY_PLAN_HOME}
            handleFormOrNext={isErrorHandling}
            size={constants.SIZE}
            moduleName="navigateHistoryBack"
          />
        </Box>
      )}
      {!easyHomeInfoLoading && easyHomeApiFailure && (
        <DynamicModal
          open={easyHomeApiFailure}
          type="API_FAILED"
          heading={constants.API_FAILED_TITLE}
          description={constants.API_FAILED_CONTENT}
          description2={constants.API_FAILED_CONTENTSECOND}
          saveBtn={constants.OK}
          onClose={closeAPIFailureModal}
        />
      )}
    </StackLayout>
  );
}
